import { observable, computed, action } from 'mobx';
import { emit, events } from 'elc-service-bus';
import { ViewController } from 'elc-service';
import { IProductRating } from '../../exported/product-rating/ProductRatingWrapper';
import { ProductRepository } from '../repositories/ProductRepository';
import { NO_VALUE } from '../constants/NoValue';
import { DEFAULT_NUMBER_OF_STARS, REVIEW_CATEGORY } from '../constants/ProductReview';

export class ProductRatingViewController extends ViewController {
    @observable public selectedRatingIndex: number = -1;
    @observable public productRepository: ProductRepository;
    @observable private numberOfStars: number = DEFAULT_NUMBER_OF_STARS;
    @observable private _productId: string = '';

    constructor(productRepository: ProductRepository) {
        super();
        this.productRepository = productRepository;
    }

    @computed
    public get product() {
        return this.productRepository.getProductById(this._productId);
    }

    @computed
    public get isLoaded() {
        return Boolean(this._productId && this.product && this.product.reviews);
    }

    @computed
    public get data() {
        return {
            selectedRatingIndex: this.selectedRatingIndex,
            percentageRating: this.percentageRating,
            onReadReviewsClick: this.onReadReviewsClick,
            onWriteReviewClick: this.onWriteReviewClick,
            numberOfReviews: this.numberOfReviews,
            isLoaded: this.isLoaded,
            averageRating: this.averageRating
        };
    }

    @action
    public initialize = (props: IProductRating) => {
        const { productId, numberOfStars } = props.config;
        this._productId = productId;
        this.numberOfStars = numberOfStars;

        this.isLoading = false;
    };

    public onReadReviewsClick = () => {
        emit(events.READ_REVIEWS_LINK_CLICKED, this.getReviewPayload());
    };

    public onWriteReviewClick = () => {
        emit(events.WRITE_REVIEWS_LINK_CLICKED, this.getReviewPayload());
    };

    private getReviewPayload() {
        const { productId, displayName } = this.product;

        return {
            id: productId,
            name: displayName,
            reviewCategory: REVIEW_CATEGORY
        };
    }

    @computed
    public get percentageRating() {
        const { reviews } = this.product || {};
        const defaultVal = undefined;
        if (reviews && 'averageRating' in reviews) {
            const { averageRating } = reviews;

            return averageRating === NO_VALUE
                ? defaultVal
                : (averageRating * 100) / this.numberOfStars;
        }

        return defaultVal;
    }

    @computed
    private get reviews() {
        return this.product && this.product.reviews;
    }

    @computed
    private get numberOfReviews() {
        return this.reviews && this.reviews.numberOfReviews;
    }

    @computed
    private get averageRating() {
        return this.reviews && this.reviews.averageRating;
    }
}
